import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Plus, Trash2, Star, Loader2, X, BarChart3, MessageSquare, Power, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminSurveys() {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [resultsTarget, setResultsTarget] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        apiPrivate.get("/admin/surveys").then(r => setSurveys(r.data || [])).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const deactivate = async (id) => {
        if (!window.confirm("Disattivare il sondaggio? Le risposte raccolte restano consultabili.")) return;
        await apiPrivate.post(`/admin/surveys/${id}/deactivate`);
        toast.success("Sondaggio disattivato");
        load();
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-surveys">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Survey</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">
                            Ascolta i <span className="font-serif-italic text-gradient-tropical">soci</span>.
                        </h1>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Crea sondaggi di feedback non bloccanti. Solo un sondaggio attivo alla volta; quando ne pubblichi uno nuovo, il precedente viene archiviato.
                        </p>
                    </div>
                    <button onClick={() => setShowForm(true)} className="btn-rebel" data-testid="new-survey-btn">
                        <Plus className="h-4 w-4" /> Nuovo sondaggio
                    </button>
                </div>

                {loading ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : surveys.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8" data-testid="surveys-empty">
                        <ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessun sondaggio creato. Clicca "Nuovo sondaggio" per iniziare.
                    </div>
                ) : (
                    <div className="space-y-3 mt-8" data-testid="surveys-list">
                        {surveys.map(s => (
                            <div key={s.id} className={`bento p-5 ${s.is_active ? "border-l-4 border-l-bayside" : "opacity-70"}`} data-testid={`survey-row-${s.id}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            {s.is_active ? (
                                                <span className="text-[9px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 rounded-full uppercase tracking-widest font-grotesk">Attivo</span>
                                            ) : (
                                                <span className="text-[9px] font-bold text-zinc-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-widest font-grotesk">Archiviato</span>
                                            )}
                                            <span className="text-xs text-zinc-500 font-grotesk">{new Date(s.created_at).toLocaleString("it-IT")}</span>
                                        </div>
                                        <h3 className="font-display text-xl leading-tight">{s.title}</h3>
                                        {s.description && <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{s.description}</p>}
                                        <div className="flex items-center gap-4 mt-3 text-xs font-grotesk text-zinc-500">
                                            <span>{s.questions.length} domande</span>
                                            <span>·</span>
                                            <span>{s.response_count} risposte</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={() => setResultsTarget(s)} className="btn-ghost text-xs" data-testid={`results-${s.id}`}>
                                            <BarChart3 className="h-3.5 w-3.5" /> Risultati
                                        </button>
                                        {s.is_active && (
                                            <button onClick={() => deactivate(s.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-vermillion border border-vermillion/30 hover:bg-vermillion/10 px-3 py-2 rounded-full transition" data-testid={`deactivate-${s.id}`}>
                                                <Power className="h-3.5 w-3.5" /> Disattiva
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {showForm && <SurveyForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
                {resultsTarget && <SurveyResults survey={resultsTarget} onClose={() => setResultsTarget(null)} />}
            </div>
        </AppLayout>
    );
}

function SurveyForm({ onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [questions, setQuestions] = useState([
        { id: "q1", text: "", type: "rating" },
    ]);
    const [saving, setSaving] = useState(false);

    const addQuestion = () => {
        if (questions.length >= 10) return;
        const nextId = `q${questions.length + 1}`;
        setQuestions([...questions, { id: nextId, text: "", type: "rating" }]);
    };
    const removeQuestion = (idx) => setQuestions(qs => qs.filter((_, i) => i !== idx));
    const updateQ = (idx, field, value) => setQuestions(qs => qs.map((q, i) => i === idx ? { ...q, [field]: value } : q));

    const submit = async () => {
        setSaving(true);
        try {
            await apiPrivate.post("/admin/surveys", { title, description, questions });
            toast.success("Sondaggio pubblicato");
            onCreated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
            setSaving(false);
        }
    };

    const canSubmit = title.length >= 3 && questions.every(q => q.text.length >= 2);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-form-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-7">
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <span className="label-tag">— Nuovo sondaggio</span>
                            <h2 className="font-display text-2xl tracking-tight mt-2">Crea un sondaggio.</h2>
                        </div>
                        <button onClick={onClose}><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 font-grotesk">
                        Pubblicando, eventuali sondaggi attualmente attivi verranno archiviati.
                    </p>

                    <div className="space-y-4 mt-5">
                        <div>
                            <div className="label-tag mb-2">Titolo *</div>
                            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Soddisfazione Q2 2026" data-testid="sf-title" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2.5 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl" />
                        </div>
                        <div>
                            <div className="label-tag mb-2">Descrizione</div>
                            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Aiutaci a migliorare..." data-testid="sf-description" className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk text-sm focus:outline-none focus:border-bayside resize-none rounded-xl" />
                        </div>

                        {/* Questions */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <div className="label-tag">Domande *</div>
                                <button onClick={addQuestion} disabled={questions.length >= 10} data-testid="sf-add-q" className="text-xs uppercase tracking-widest font-grotesk text-bayside hover:text-white">
                                    + Aggiungi domanda
                                </button>
                            </div>
                            <div className="space-y-3">
                                {questions.map((q, idx) => (
                                    <div key={q.id} className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 flex items-start gap-3" data-testid={`sf-q-${idx}`}>
                                        <span className="text-xs text-zinc-500 font-grotesk mt-2 shrink-0">{idx + 1}.</span>
                                        <div className="flex-1">
                                            <input
                                                value={q.text}
                                                onChange={(e) => updateQ(idx, "text", e.target.value)}
                                                placeholder="Testo della domanda…"
                                                data-testid={`sf-q-text-${idx}`}
                                                className="w-full bg-transparent text-white font-grotesk text-sm focus:outline-none placeholder-zinc-500"
                                            />
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    onClick={() => updateQ(idx, "type", "rating")}
                                                    data-testid={`sf-q-type-rating-${idx}`}
                                                    className={`text-[10px] uppercase tracking-widest font-grotesk px-2 py-1 rounded-full border ${q.type === "rating" ? "border-bayside bg-bayside/15 text-white" : "border-white/10 text-zinc-500"}`}
                                                >
                                                    <Star className="h-3 w-3 inline mr-1" /> Rating 1-5
                                                </button>
                                                <button
                                                    onClick={() => updateQ(idx, "type", "text")}
                                                    data-testid={`sf-q-type-text-${idx}`}
                                                    className={`text-[10px] uppercase tracking-widest font-grotesk px-2 py-1 rounded-full border ${q.type === "text" ? "border-bayside bg-bayside/15 text-white" : "border-white/10 text-zinc-500"}`}
                                                >
                                                    <MessageSquare className="h-3 w-3 inline mr-1" /> Risposta aperta
                                                </button>
                                            </div>
                                        </div>
                                        {questions.length > 1 && (
                                            <button onClick={() => removeQuestion(idx)} data-testid={`sf-q-remove-${idx}`} className="text-zinc-600 hover:text-vermillion shrink-0 mt-1">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button onClick={onClose} className="btn-ghost" data-testid="sf-cancel">Annulla</button>
                        <button onClick={submit} disabled={saving || !canSubmit} className="btn-rebel" data-testid="sf-publish">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pubblica sondaggio"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SurveyResults({ survey, onClose }) {
    const [results, setResults] = useState(null);

    useEffect(() => {
        apiPrivate.get(`/admin/surveys/${survey.id}/results`).then(r => setResults(r.data));
    }, [survey.id]);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-results-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-7">
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <span className="label-tag">— Risultati</span>
                            <h2 className="font-display text-2xl tracking-tight mt-2">{survey.title}</h2>
                        </div>
                        <button onClick={onClose}><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>
                    {!results ? (
                        <div className="p-10 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                    ) : (
                        <>
                            <div className="bento p-4 mt-4 flex items-center gap-4">
                                <div className="font-display text-4xl text-gradient-tropical">{results.total_responses}</div>
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Risposte totali</div>
                            </div>
                            <div className="space-y-5 mt-6">
                                {results.questions.map(q => (
                                    <div key={q.question_id} className="bento p-5" data-testid={`result-q-${q.question_id}`}>
                                        <h3 className="font-display text-base leading-tight">{q.question_text}</h3>
                                        {q.type === "rating" ? (
                                            <div className="mt-4">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <span className="font-display text-3xl text-bayside">{q.avg_rating ?? "—"}</span>
                                                    <span className="text-xs text-zinc-500 font-grotesk uppercase tracking-widest">media su 5</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {[5, 4, 3, 2, 1].map(n => {
                                                        const count = q.distribution[String(n)] || 0;
                                                        const pct = results.total_responses ? Math.round(100 * count / results.total_responses) : 0;
                                                        return (
                                                            <div key={n} className="flex items-center gap-3 text-xs font-grotesk">
                                                                <span className="text-zinc-400 w-6">{n}★</span>
                                                                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-[linear-gradient(90deg,#ed4735,#df46e7,#653fcd)]" style={{ width: `${pct}%` }} />
                                                                </div>
                                                                <span className="text-zinc-400 w-12 text-right">{count} ({pct}%)</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                                                {q.text_responses.length === 0 ? (
                                                    <p className="text-zinc-500 text-sm italic">Nessuna risposta.</p>
                                                ) : (
                                                    q.text_responses.map((t, i) => (
                                                        <div key={`${i}-${(t || "").slice(0, 30)}`} className="text-sm text-zinc-300 bg-white/5 rounded-xl p-3 font-grotesk">
                                                            "{t}"
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
